import { useState, useCallback, useRef } from 'react'
import { Loader2, Upload, CheckCircle, Phone, XCircle, Briefcase, DollarSign, CreditCard, Clock, TrendingUp, Wallet, BarChart3, FileSpreadsheet } from 'lucide-react'
import type { ColdProsInitialData, ColdProsAnalytics, ColdProsSummary } from './types'
import { fetchStats, uploadFile } from './api'
import { cn } from '@/lib/utils'
import { ConversionFunnel } from '../elocal-analytics/components/ConversionFunnel'
import { PaymentFunnel } from '../elocal-analytics/components/PaymentFunnel'
import { JobTypesBreakdown } from './components/JobTypesBreakdown'
import { ColdProsLeadsTable } from './components/ColdProsLeadsTable'

interface Props { initialData: ColdProsInitialData }

function fmt(val: number): string { return `$${val.toLocaleString('en-US')}` }

interface CardProps { title: string; icon: React.ElementType; value: string | number; subtitle: string; iconColor?: string; valueColor?: string }
function Card({ title, icon: Icon, value, subtitle, iconColor = 'text-gray-400', valueColor = 'text-gray-900' }: CardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <p className={cn('text-3xl font-bold', valueColor)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  )
}

function SummaryCards({ data }: { data: ColdProsSummary }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-5 gap-4">
        <Card title="Calls" icon={Phone} value={data.calls} subtitle="CallRail total" iconColor="text-blue-500" />
        <Card title="Booked" icon={BarChart3} value={data.booked} subtitle={`${fmt(data.cost_per_lead)} per call`} iconColor="text-purple-500" valueColor="text-purple-600" />
        <Card title="Cancelled" icon={XCircle} value={data.cancelled} subtitle={`${data.booked > 0 ? ((data.cancelled/data.booked)*100).toFixed(0) : 0}% cancel rate`} iconColor="text-red-500" valueColor="text-red-500" />
        <Card title="Jobs" icon={Briefcase} value={data.jobs} subtitle={`${fmt(data.cost_per_job)} per job`} iconColor="text-green-500" valueColor="text-green-600" />
        <Card title="Not Booked" icon={Phone} value={data.calls - data.booked} subtitle="No Workiz match" iconColor="text-orange-500" valueColor="text-orange-600" />
      </div>
      <div className="grid grid-cols-5 gap-4">
        <Card title="Spent" icon={DollarSign} value={fmt(data.spent)} subtitle={`${data.total_clicks.toLocaleString()} clicks`} iconColor="text-red-500" valueColor="text-red-600" />
        <Card title="Collected" icon={CreditCard} value={fmt(data.collected)} subtitle="Paid payments" iconColor="text-green-500" valueColor="text-green-600" />
        <Card title="Outstanding" icon={Clock} value={fmt(data.outstanding)} subtitle="Revenue - Collected" iconColor="text-amber-500" valueColor="text-amber-600" />
        <Card title="Revenue" icon={TrendingUp} value={fmt(data.revenue)} subtitle="Job totals" iconColor="text-blue-500" />
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Company Net</h3>
            <Wallet className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600">{fmt(data.company_net)}</p>
          <div className="text-xs text-gray-400 mt-1 space-y-0.5">
            <p>{fmt(data.revenue)} - 10% = {fmt(data.after_parts)}</p>
            <p>{fmt(data.after_parts)} / 2 = <span className="font-semibold text-emerald-600">{fmt(data.company_net)}</span></p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ColdProsAnalyticsDashboard({ initialData }: Props) {
  const [analytics, setAnalytics] = useState<ColdProsAnalytics | null>(initialData.analytics)
  const [loading, setLoading] = useState(false)
  const [callrailUploaded, setCallrailUploaded] = useState(initialData.callrail_uploaded)
  const [spendUploaded, setSpendUploaded] = useState(initialData.spend_uploaded)
  const [uploading, setUploading] = useState<string | null>(null)
  const crRef = useRef<HTMLInputElement>(null)
  const adsRef = useRef<HTMLInputElement>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try { setAnalytics(await fetchStats()) } catch {} finally { setLoading(false) }
  }, [])

  const handleUpload = useCallback(async (type: string, file: File) => {
    setUploading(type)
    try {
      await uploadFile(type, file)
      if (type === 'coldpros_callrail') setCallrailUploaded(true)
      if (type === 'coldpros_google_ads') setSpendUploaded(true)
      refresh()
    } catch {} finally { setUploading(null) }
  }, [refresh])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cold Pros AdWords</h1>
        <p className="text-sm text-gray-500 mt-1">ColdPros Commercial Appliance Service — Google Ads effectiveness</p>
      </div>

      {/* Upload Panel */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload Data Files</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${callrailUploaded ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'}`}>
            <input ref={crRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload('coldpros_callrail', f); e.target.value = '' }} />
            {callrailUploaded ? <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" /> : <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-gray-400" />}
            <p className="text-sm font-medium text-gray-700">CallRail Calls</p>
            <p className="text-xs text-gray-500 mt-1">XLSX export from CallRail</p>
            <button onClick={() => crRef.current?.click()} disabled={uploading === 'coldpros_callrail'}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {uploading === 'coldpros_callrail' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {callrailUploaded ? 'Re-upload' : 'Upload'}
            </button>
          </div>
          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${spendUploaded ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'}`}>
            <input ref={adsRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload('coldpros_google_ads', f); e.target.value = '' }} />
            {spendUploaded ? <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" /> : <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-400" />}
            <p className="text-sm font-medium text-gray-700">Google Ads Spend</p>
            <p className="text-xs text-gray-500 mt-1">Time_series CSV from Google Ads</p>
            <button onClick={() => adsRef.current?.click()} disabled={uploading === 'coldpros_google_ads'}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {uploading === 'coldpros_google_ads' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {spendUploaded ? 'Re-upload' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /><span className="ml-3 text-gray-500">Loading analytics...</span></div>}

      {!loading && analytics && (
        <>
          <SummaryCards data={analytics.summary} />
          <JobTypesBreakdown data={analytics.job_types} />
          <div className="grid grid-cols-2 gap-6">
            <ConversionFunnel funnel={analytics.funnel} />
            <PaymentFunnel data={analytics.payment_funnel} />
          </div>
          <ColdProsLeadsTable initialLeads={analytics.leads_detail.slice(0, 25)} totalLeads={analytics.leads_detail.length} />
        </>
      )}

      {!loading && !analytics && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
          <p className="text-gray-500 text-lg">Upload CallRail XLSX to see analytics</p>
        </div>
      )}
    </div>
  )
}
